/**
 * The Affiliate page: this account's link, what it brought, what it earned and
 * how it is paid (`affiliate-routes.ts` on the Worker).
 *
 * Opening the page is joining the programme — the Worker makes the code on the
 * first read — so there is no "Join" step to fail at. Payouts need a Stripe
 * Express account, opened from here; until Stripe says it can be paid, the
 * earnings simply wait.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { Callout, Empty, GhostButton, Kicker, PrimaryButton, Row, StatStrip, StatusCell, Table } from '../components/primitives';
import { SectionHead } from '../components/Section';
import { notify } from '../components/Toast';
import { api, ApiError } from '../lib/api';
import { isoDay, money } from '../lib/format';
import { TaxDetailsSection, type TaxState } from './affiliate-tax';

interface Entry {
  id: string;
  kind: 'commission' | 'reversal' | 'reinstated' | 'adjustment';
  invoiceId: string | null;
  baseCents: number;
  amountCents: number;
  currency: string;
  availableAt: string;
  payoutId: string | null;
  note: string | null;
  createdAt: string;
}

interface Payout {
  id: string;
  amountCents: number;
  currency: string;
  status: 'sending' | 'paid' | 'failed' | 'review' | 'rejected';
  createdAt: string;
  paidAt: string | null;
}

interface Summary {
  code: string;
  link: string;
  status: 'active' | 'suspended';
  payouts: { connected: boolean; enabled: boolean; country: string | null };
  terms: { ratePercent: number; holdDays: number; minPayoutCents: number; countries: string[] };
  referrals: { accounts: number; organizations: number; paying: number };
  balance: { pendingCents: number; availableCents: number; paidCents: number };
  tax: TaxState;
  entries: Entry[];
  history: Payout[];
}

const KIND: Record<Entry['kind'], string> = {
  commission: 'Commission',
  reversal: 'Taken back',
  reinstated: 'Given back',
  adjustment: 'Adjustment'
};

/** A signed amount: money() has no minus sign of its own. */
function signed(cents: number, currency: string): string {
  return cents < 0 ? `−${money(-cents, currency)}` : money(cents, currency);
}

export function Affiliate({ siteUrl }: { siteUrl: string }): React.JSX.Element {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void api
      .get<Summary>('/affiliate')
      .then(setSummary)
      .catch(() => setFailed(true));
  }, []);

  if (failed) return <Empty title="The affiliate programme could not be read">Reload the page in a moment.</Empty>;
  if (!summary) return <Empty title="Reading your affiliate account…" />;

  const { balance, referrals, terms } = summary;
  return (
    <>
      {summary.status === 'suspended' && (
        <Callout tone="amber" title="This affiliate account is suspended">
          Your link no longer brings anybody, and nothing is paid out. Write to support@tryairlock.app if you think this
          is a mistake.
        </Callout>
      )}
      <LinkTile link={summary.link} />
      <div style={{ marginTop: 12 }}>
        <StatStrip
          items={[
            {
              label: 'Brought',
              value: String(referrals.accounts + referrals.organizations),
              note: `${referrals.paying} paying`
            },
            { label: 'Waiting', value: money(balance.pendingCents, 'eur'), note: `Held ${terms.holdDays} days` },
            { label: 'Available', value: signed(balance.availableCents, 'eur'), note: `Paid from ${money(terms.minPayoutCents, 'eur')}` },
            { label: 'Paid out', value: money(balance.paidCents, 'eur') }
          ]}
        />
      </div>

      <Payouts summary={summary} />
      <TaxDetailsSection
        tax={summary.tax}
        countries={summary.terms.countries}
        onSaved={(details) => setSummary({ ...summary, tax: { ...summary.tax, details } })}
      />

      <SectionHead title="Earnings" description="One line per invoice your customers paid, and per refund or dispute that took one back." />
      <Entries entries={summary.entries} />

      {summary.history.length > 0 && (
        <>
          <SectionHead title="Payouts" />
          <History payouts={summary.history} />
        </>
      )}

      <p style={{ fontSize: 13, color: color.inkFaint, margin: '20px 0 0', lineHeight: 1.6 }}>
        You earn {terms.ratePercent}% of what each customer you bring pays, excluding VAT, on every invoice for as long
        as they pay. A customer is yours if they sign up within 90 days of following your link, or buy for the first
        time in that window. Each commission waits {terms.holdDays} days, because a cardholder can dispute a payment for
        up to about that long. Payouts go out on the 1st of each month. Read the{' '}
        <a href={`${siteUrl}/legal/affiliate`} target="_blank" rel="noreferrer">
          affiliate terms
        </a>
        .
      </p>
    </>
  );
}

function LinkTile({ link }: { link: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div style={{ background: color.surfaceAlt, borderRadius: radius.xxl, padding: '28px 32px' }}>
      <Kicker>Your link</Kicker>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
        <output
          aria-label={`Affiliate link ${link}`}
          style={{
            flex: '1 1 320px',
            minWidth: 0,
            fontFamily: font.mono,
            fontSize: 20,
            lineHeight: 1.3,
            color: color.ink,
            userSelect: 'all',
            overflowWrap: 'anywhere'
          }}
        >
          {link}
        </output>
        <PrimaryButton size="sm" onClick={() => void copy()}>
          <span aria-live="polite">{copied ? 'Copied' : 'Copy link'}</span>
        </PrimaryButton>
      </div>
    </div>
  );
}

/** Getting paid: open the Stripe account, finish it, or reach its dashboard. */
function Payouts({ summary }: { summary: Summary }): React.JSX.Element {
  const [country, setCountry] = useState(summary.payouts.country ?? 'FR');
  const [busy, setBusy] = useState(false);

  const go = async (path: '/affiliate/connect' | '/affiliate/dashboard'): Promise<void> => {
    setBusy(true);
    try {
      const answer = await api.post<{ url: string }>(path, path === '/affiliate/connect' ? { country } : {});
      window.location.assign(answer.url);
    } catch (error) {
      notify(error instanceof ApiError ? error.message : 'Stripe could not be reached.');
      setBusy(false);
    }
  };

  const { connected, enabled } = summary.payouts;
  return (
    <>
      <SectionHead
        title="Getting paid"
        description="Payouts go through Stripe to a bank account in the EEA, the United Kingdom, Switzerland, the United States or Canada. Stripe asks for your identity and bank details and keeps them; Airlock never sees them."
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {enabled ? (
          <>
            <StatusCell tone="ok" word="Ready to be paid" />
            <GhostButton size="sm" onClick={() => void go('/affiliate/dashboard')}>
              Open your Stripe dashboard
            </GhostButton>
          </>
        ) : connected ? (
          <>
            <StatusCell tone="warn" word="Stripe needs more from you" />
            <PrimaryButton size="sm" busy={busy} onClick={() => void go('/affiliate/connect')}>
              Finish with Stripe
            </PrimaryButton>
          </>
        ) : (
          <>
            <select
              aria-label="Country of your bank account"
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              style={{
                height: 32,
                padding: '0 11px',
                border: `1px solid ${color.lineInput}`,
                borderRadius: 8,
                background: color.surface,
                fontFamily: 'inherit',
                fontSize: 13,
                color: color.ink,
                cursor: 'pointer'
              }}
            >
              {summary.terms.countries.map((code) => (
                <option key={code} value={code}>
                  {countryName(code)}
                </option>
              ))}
            </select>
            <PrimaryButton size="sm" busy={busy} disabled={summary.status !== 'active'} onClick={() => void go('/affiliate/connect')}>
              Set up payouts with Stripe
            </PrimaryButton>
          </>
        )}
      </div>
    </>
  );
}

function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

function Entries({ entries }: { entries: Entry[] }): React.JSX.Element {
  const columns = '110px 1fr 120px 120px 150px';
  if (entries.length === 0) {
    return <Empty title="Nothing earned yet">Your first line appears when a customer you brought pays an invoice.</Empty>;
  }
  return (
    <Table columns={columns} header={['Date', 'What', 'Paid excl. VAT', 'Yours', 'Status']}>
      {entries.map((entry) => {
        const held = !entry.payoutId && new Date(entry.availableAt).getTime() > Date.now();
        return (
          <Row key={entry.id} columns={columns}>
            <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }}>{isoDay(entry.createdAt)}</span>
            <span>
              {KIND[entry.kind]}
              {entry.note && entry.kind !== 'commission' ? ` — ${entry.note}` : ''}
            </span>
            <span style={{ color: color.inkSubtle }}>{entry.baseCents ? money(entry.baseCents, entry.currency) : '—'}</span>
            <span>{signed(entry.amountCents, entry.currency)}</span>
            {entry.payoutId ? (
              <StatusCell tone="ok" word="Paid out" />
            ) : held ? (
              <StatusCell tone="off" ring word="Waiting" detail={`until ${isoDay(entry.availableAt)}`} />
            ) : (
              <StatusCell tone="ok" ring word="Available" />
            )}
          </Row>
        );
      })}
    </Table>
  );
}

function History({ payouts }: { payouts: Payout[] }): React.JSX.Element {
  const columns = '110px 1fr 150px';
  return (
    <Table columns={columns} header={['Date', 'Amount', 'Status']}>
      {payouts.map((payout) => (
        <Row key={payout.id} columns={columns}>
          <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }}>{isoDay(payout.createdAt)}</span>
          <span>{money(payout.amountCents, payout.currency)}</span>
          <StatusCell
            tone={payout.status === 'paid' ? 'ok' : payout.status === 'failed' || payout.status === 'rejected' ? 'bad' : 'warn'}
            word={
              payout.status === 'paid'
                ? 'Sent'
                : payout.status === 'failed'
                  ? 'Failed — retried next month'
                  : payout.status === 'review'
                    ? 'Being checked'
                    : payout.status === 'rejected'
                      ? 'Not sent — write to support'
                      : 'Sending'
            }
          />
        </Row>
      ))}
    </Table>
  );
}
